import { useState } from 'react';
import { ChevronDownIcon as ArrowIcon } from '@heroicons/react/24/solid';
import { useProductList } from '../../context';
import useDynamicFacets from '../../hooks/useDynamicFacets';

export interface DesktopFacetsProps {
  pagination?: {
    currentPage: number;
    totalPages: number;
    limit?: number;
  };
}

const DesktopFacets: React.FC<DesktopFacetsProps> = ({ pagination }) => {
  const { facetsConfiguration, totalItems, activeRefinements, removeAllRefinements } = useProductList();

  const [openFacets, setOpenFacets] = useState<Record<string, boolean>>({
    price: true,
  });

  const toggleFacetVisibility = (attribute: string) => {
    setOpenFacets((prev) => ({
      ...prev,
      [attribute]: !prev[attribute],
    }));
  };

  const facets = useDynamicFacets({
    configuration: facetsConfiguration,
    render: ({ attribute, Component }) => {
      const isFacetOpen = openFacets[attribute] ?? false;
      return (
        <div className="relative" key={attribute}>
          <div
            className={`flex cursor-pointer items-center justify-between ${attribute === 'variants.scopedPrice.value' ? '' : 'border-b border-gray-300'} py-16`}
            onClick={() => toggleFacetVisibility(attribute)}
          >
            <span className="text-base font-bold text-secondary-black group-hover:text-blue-600 group-active:text-gray-100">
              {facetsConfiguration[attribute].label}
            </span>
            {attribute !== 'variants.scopedPrice.value' && (
              <ArrowIcon
                className={`mt-2 w-16 stroke-secondary-black ${isFacetOpen ? 'rotate-180' : ''} group-hover:stroke-blue-600 group-active:stroke-gray-100`}
              />
            )}
          </div>
          {attribute === 'variants.scopedPrice.value' || isFacetOpen ? <div>{Component}</div> : null}
        </div>
      );
    },
  });
  const getPaginationText = (
    totalItems: number,
    pagination?: { currentPage: number; limit?: number; totalPages: number },
  ) => {
    const currentPage = pagination?.currentPage ?? 1;
    const limit = pagination?.limit ?? totalItems;
    if (totalItems <= limit) {
      return `Showing 1 to ${totalItems} out of ${totalItems} results`;
    }

    const start = (currentPage - 1) * limit + 1;
    const end = Math.min(start + limit - 1, totalItems);

    return `Showing ${start} to ${end} out of ${totalItems} results`;
  };
  return (
    <div className="hidden w-full border-b border-neutral-400 pb-16 lg:flex lg:flex-col">
      <div className="mb-24">
        <p className="text-secondary-black">{getPaginationText(totalItems, pagination)}</p>
      </div>

      <div className="mb-20 flex w-full items-center justify-between">
        <span className="text-xl font-bold text-secondary-black">Filters ({activeRefinements.length})</span>
        {activeRefinements.length ? (
          <span className="font- cursor-pointer text-14 text-blue-500" onClick={removeAllRefinements}>
            Clear All
          </span>
        ) : (
          <></>
        )}
      </div>
      {facets}
    </div>
  );
};

export default DesktopFacets;
